import React, { useState, useEffect } from 'react';
import { CheckCircle } from 'lucide-react';
import { TbSunset2 } from "react-icons/tb";
import { GiMountainRoad } from "react-icons/gi";
import OptimizedSearchAutocomplete from './OptimizedSearchAutocomplete';
import api from '../services/api'; // הוסף את זה אם לא קיים
import { useLocation } from 'react-router-dom';
import { useNavigation } from '../hooks/useNavigation';
const Header = React.forwardRef(({ 
  selectedCategory, 
  currentCategory, 

  goToHomePage, 
  handleCategoryChange,
  handleSearchResults,
  loading,
  currentSearchTerm,
  statistics 
}, ref) => {
   const { savingsCategories } = useNavigation();
  const [reportPeriod, setReportPeriod] = useState(null);
  const [loadingReportDate, setLoadingReportDate] = useState(true);
  const [imageErrors, setImageErrors] = useState({}); // לעקוב אחרי תמונות שלא נטענו
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  
  // State לעקוב אחרי קטגוריה פעילה
  const [hasActiveCategory, setHasActiveCategory] = useState(false);
  
  // עדכון הסטטוס כשהקטגוריה משתנה
  useEffect(() => {
    setHasActiveCategory(!!currentCategory);
  }, [currentCategory]);
  
  // טעינת תאריך הדיווח האחרון בעת טעינת הקומפוננט
  useEffect(() => {
    loadLatestReportPeriod();
  }, []);

  const loadLatestReportPeriod = async () => {
    try {
      setLoadingReportDate(true);
      
      // נסה לטעון נתונים מקטגוריה כלשהי לקבלת תאריך הדיווח
      const defaultCategory = selectedCategory || 'study-funds';
      const data = await api.getProducts(defaultCategory);
      
      if (data && data.length > 0 && data[0].reportPeriod) {
        setReportPeriod(data[0].reportPeriod);
      }
    } catch (error) {
      console.warn('❌ Failed to load report period:', error);
    } finally {
      setLoadingReportDate(false);
    }
  };

  // פונקציה לטיפול בכשל טעינת תמונה
  const handleImageError = (categoryId) => {
    setImageErrors(prev => ({
      ...prev,
      [categoryId]: true
    }));
  };

  // פונקציה להמרת תקופת דיווח לתאריך יפה
  const getFormattedReportDate = () => {
    if (!reportPeriod) return null;
    
    const months = [
      'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
      'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'
    ];
    
    const periodStr = reportPeriod.toString();
    if (periodStr.length === 6) {
      const year = periodStr.slice(0, 4);
      const monthNum = parseInt(periodStr.slice(-2));
      
      if (monthNum >= 1 && monthNum <= 12) {
        return `${months[monthNum - 1]} ${year}`;
      }
    }
    
    return null;
  };

  const formattedDate = getFormattedReportDate();

  // פונקציה לקבלת ברכה לפי שעה
  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();
    
    // 0:00-11:59 - בוקר טוב
    if (hour >= 0 && hour < 12) {
      return { text: 'בוקר טוב', icon: TbSunset2 };
    } 
    // 12:00-16:59 - צהריים טובים
    else if (hour >= 12 && hour < 17) {
      return { text: 'צהריים טובים', emoji: '🌴' };
    } 
    // 17:00-21:59 - ערב טוב
    else if (hour >= 17 && hour < 22) {
      return { text: 'ערב טוב', icon: GiMountainRoad };
    } 
    // 22:00-04:59 - לילה טוב
    else {
      return { text: 'לילה טוב', emoji: '🌖' };
    }
  };

  const greeting = getTimeBasedGreeting();

  return (
    <section
      ref={ref}
    className={`relative bg-cover bg-center bg-no-repeat ${isHomePage ? 'md:pt-20 pt-12 ' : 'md:pt-8 pt-6'} text-white pb-1 md:pb-1 header-bg`}
      style={{ overflow: 'visible' }}
    >
      {/* רקע אנימציה מתקדם */}
      <div className="absolute inset-0">
        {/* גרדיינט רקע עם שכבות */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 via-indigo-900/60 to-purple-900/80"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-400/20 via-transparent to-transparent"></div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" style={{overflow: 'visible'}}>
        
        {/* פריסה ראשית - במובייל: לוגו משמאל ומלל מימין, בדסקטופ: לוגו מימין ומלל משמאל */}
        <div className="flex flex-row items-center justify-between gap-2 lg:gap-6 mb-3 md:mb-4">
          
          {/* לוגו בצד שמאל במובייל, בצד ימין בדסקטופ */}
          <div className="order-2 flex-shrink-0">
            <div className="relative group">
              {/* הילה עדינה מאוד */}
              <div className="absolute -inset-1 lg:-inset-3 bg-gradient-to-r from-blue-400/8 via-purple-400/12 to-blue-400/8 rounded-3xl blur-3xl opacity-40 group-hover:opacity-60 transition-all duration-700"></div>
              
              {/* הלוגו עם דהייה רק בקצוות */}
              <img 
                src="/logo.gif" 
                alt="פרו גמל נט" 
                className="relative w-40 h-70 lg:w-80 lg:h-60 object-contain group-hover:scale-105 transition-all duration-500 cursor-pointer"
                style={{
                  opacity: 0.85,
                  maskImage: 'linear-gradient(to right, transparent 0%, black 15%, black 85%, transparent 100%), linear-gradient(to bottom, transparent 0%, black 10%, black 90%, transparent 100%)',
                  WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 15%, black 85%, transparent 100%), linear-gradient(to bottom, transparent 0%, black 10%, black 90%, transparent 100%)',
                  maskComposite: 'intersect'
                }}
                onClick={goToHomePage}
              />
            </div>
          </div>

          {/* תוכן בצד ימין במובייל, בצד שמאל בדסקטופ */}
<div className="order-1 lg:order-1 flex-1 text-center sm:text-right lg:text-right px-2 sm:px-0">
  <h2 className="text-sm sm:text-lg md:text-xl lg:text-4xl font-bold mb-2 sm:mb-3 lg:mb-4 leading-snug sm:leading-snug lg:leading-[1.2]">
    <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
      גמלנט AI מאפשר לך לזהות
    </span>
    <br className="lg:hidden" />
    <span className="bg-gradient-to-r from-yellow-300 to-orange-400 bg-clip-text text-transparent">
      {' '}בלחיצת כפתור אחת
    </span>
    <br />
    <span className="bg-gradient-to-r from-cyan-300 to-purple-400 bg-clip-text text-transparent">
      את ההשקעות המובילות – בהתאם לפרופיל הסיכון האישי שלך
    </span>
  </h2>

  <div className="text-sm md:text-base lg:text-lg mb-3 sm:mb-4 lg:mb-6 leading-snug max-w-4xl mx-auto lg:mx-0 space-y-2 sm:space-y-3 lg:space-y-3">
    <p className="font-bold bg-gradient-to-r from-blue-200 to-cyan-200 bg-clip-text text-transparent">
      המערכת מבוססת על בינה מלאכותית מתקדמת,
    </p>
    
    <p className="font-bold bg-gradient-to-r from-blue-200 to-cyan-200 bg-clip-text text-transparent">
      המחוברת ישירות למקורות המידע הרשמיים של משרד האוצר –{' '}
      <span className="lg:inline">גמל־נט, פנסיה־נט וביטוח־נט.</span>
    </p>
    
  
    <p className="font-bold">
      <span className="bg-gradient-to-r from-blue-200 to-cyan-200 bg-clip-text text-transparent">
        השוו בין מאות מסלולים, גלו הזדמנויות והחליטו בחכמה
      </span>
      {' '}
     
    </p>
    
    {loadingReportDate && (
      <div className="inline-flex items-center px-2 sm:px-3 md:px-4 py-1 sm:py-2 bg-blue-500/20 backdrop-blur-xl rounded-full border border-blue-400/30 shadow-lg">
        <div className="animate-spin rounded-full h-2 w-2 sm:h-3 sm:w-3 border-b-2 border-blue-400 ml-1"></div>
        <span className="bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent text-xs sm:text-sm">
          טוען תאריך עדכון...
        </span>
      </div>
    )}
  </div>
</div>
        </div>

        {/* שורת כפתורי הקטגוריות - כפתורים מותאמים למובייל */}
        <div className="mb-3 md:mb-4 px-2 md:px-4">
          {/* במובייל: עיצוב מעגלי עם כפתורים מותאמים */}
          <div className="md:hidden flex justify-center items-center overflow-hidden">
            <div className="relative w-[min(320px,85vw)] h-[min(320px,85vw)] sm:w-[min(380px,90vw)] sm:h-[min(380px,90vw)] mb-3 mt-3"> 
             <style>{`
                @keyframes pulse-ring {
                  0% {
                    transform: scale(1);
                    opacity: 1;
                  }
                  50% {
                    transform: scale(1.3);
                    opacity: 0.3;
                  }
                  100% {
                    transform: scale(1.6);
                    opacity: 0;
                  }
                }
                
                @keyframes hologram {
                  0%, 100% {
                    background-position: 0% 50%;
                  }
                  50% {
                    background-position: 100% 50%;
                  }
                }
                
                @keyframes rotate-glow {
                  0% {
                    transform: rotate(0deg);
                  }
                  100% {
                    transform: rotate(360deg);
                  }
                }
                
                @keyframes beam-pulse {
                  0%, 100% {
                    opacity: 0.3;
                  }
                  50% {
                    opacity: 0.8;
                  }
                }
                
                .futuristic-btn {
                  background: conic-gradient(from 0deg, #00f2fe, #4facfe, #667eea, #764ba2, #f093fb, #f5576c, #00f2fe);
                  background-size: 200% 200%;
                  animation: hologram 4s ease-in-out infinite;
                }
                
                .center-glow {
                  background: conic-gradient(from 0deg,rgb(225, 208, 216),rgb(208, 192, 230),rgb(192, rgb(208, 231, 222)),rgb(226, 245, 238), #ffbe0b, #fb5607, #ff006e);
                  shadow: 0 0 15px rgba(0, 0, 0, 0.6), 0 0 30px rgba(16, 15, 15, 0.4);
                  animation: pulse-glow  6s linear infinite;
                }
                
                .beam {
                  animation: beam-pulse 2s ease-in-out infinite;
                }
              `}</style>
              
              {/* קרני אור רקע */}
              <div className="absolute inset-0 pointer-events-none">
                {selectedCategory && savingsCategories.map((category, index) => {
                  const isActive = selectedCategory === category.id;
                  if (!isActive) return null;
                  
                  return (
                    <div key={`beam-${category.id}`} className="absolute inset-0">
                      {/* קרניים למרכז */}
                      {[0, 1, 2, 3, 4, 5].map((beamIndex) => {
                        const angle = (beamIndex * 60) - 90; // זוויות של 60 מעלות
                        return (
                          <div
                            key={beamIndex}
                            className="beam absolute w-0.5 bg-gradient-to-r from-cyan-400/60 to-transparent"
                            style={{
                              height: '120px',
                              top: '50%',
                              left: '50%',
                              transformOrigin: 'bottom',
                              transform: `translate(-50%, -120px) rotate(${angle}deg)`,
                              animationDelay: `${beamIndex * 0.2}s`
                            }}
                          />
                        );
                      })}
                    </div>
                  );
                })}
              </div>
              
              {savingsCategories.map((category, index) => {
                const IconComponent = category.icon;
                const isActive = selectedCategory === category.id;
                const hasImageError = imageErrors[category.id];
                
                // חישוב מיקום - הכפתור הפעיל במרכז, השאר בעיגול
                let position = {};
                if (isActive) {
                  // כפתור פעיל במרכז
                  position = {
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 30
                  };
                } else {
                  // שאר הכפתורים בעיגול - רדיוס דינמי
                  const availableButtons = savingsCategories.filter(cat => cat.id !== selectedCategory);
                  const buttonIndex = availableButtons.findIndex(cat => cat.id === category.id);
                  const totalButtons = availableButtons.length;
                  const angle = (buttonIndex * (360 / totalButtons)) - 90; // מתחילים מלמעלה
                  const radius = window.innerWidth < 360 ? 90 : window.innerWidth < 400 ? 110 : 120; // רדיוס דינמי לפי רוחב המסך
                  
                  const x = Math.cos(angle * Math.PI / 180) * radius;
                  const y = Math.sin(angle * Math.PI / 180) * radius;
                  
                  position = {
                    top: '50%',
                    left: '50%',
                    transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                    zIndex: 20
                  };
                }
                
                return (
                  <div
                    key={category.id}
                    className="absolute transition-all duration-1000 ease-out"
                    style={position}
                  >
                    {/* טבעות פולס למרכז בלבד */}
                    {isActive && (
                      <>
                        <div className="absolute inset-0 pulse-ring pointer-events-none md:hidden">
                          <div className="w-[101px] h-[101px] rounded-full border-2 border-cyan-400/40 opacity-60"></div>
                        </div>
                        <div className="absolute inset-0 pulse-ring pointer-events-none md:hidden" style={{animationDelay: '0.5s'}}>
                          <div className="w-[86px] h-[86px] rounded-full border-2 border-purple-400/40 opacity-60"></div>
                        </div>
                        <div className="absolute inset-0 pulse-ring pointer-events-none hidden md:block">
                          <div className="w-28 h-28 rounded-full border-2 border-cyan-400/40 opacity-60"></div>
                        </div>
                        <div className="absolute inset-0 pulse-ring pointer-events-none hidden md:block" style={{animationDelay: '0.5s'}}>
                          <div className="w-24 h-24 rounded-full border-2 border-purple-400/40 opacity-60"></div>
                        </div>
                      </>
                    )}
                    
                    {/* הכפתור */}
                    <button
                      onClick={() => handleCategoryChange(category.id)}
                      className={`relative group transition-all duration-700 transform hover:scale-110 ${
                        isActive ? 'scale-110' : 'hover:scale-105'
                      }`}
                    >
                      {/* רקע הולוגרמי עגול */}
                      <div className={`rounded-full transition-all duration-500 ${
                        isActive 
                          ? 'center-glow w-[112px] h-[112px] md:w-28 md:h-28 p-0 md:p-0.5'
                          : 'bg-gradient-to-r from-slate-600/40 to-slate-800/40 hover:from-blue-500/30 hover:to-purple-500/30 w-[96px] h-[96px] md:w-24 md:h-24 p-0.5'
                      }`}>
                        
                        {/* הכפתור הפנימי - תמונה ללא מגבלות */}
                        <div className={`w-full h-full rounded-full relative overflow-hidden transition-all duration-500 ${
                          isActive 
                            ? 'border-0 border-white/80 shadow-2xl backdrop-blur-sm' 
                            : 'border-0 border-white/30 hover:border-white/50 backdrop-blur-sm'
                        }`}>
                          
                          {/* אפקט זוהר עדין */}
                          {isActive && (
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full animate-pulse"></div>
                          )}
                          
                          {/* התמונה או האייקון - ללא padding, מלא את כל השטח */}
                          <div className="absolute inset-0 flex items-center justify-center">
                            {!hasImageError ? (
                              <img 
                                src={`/categoryBT/${category.id}.png`}
                                alt={category.title}
                                className={`object-cover w-full h-full transition-all duration-500 ${
                                  isActive 
                                    ? 'drop-shadow-2xl filter brightness-110 scale-100'
                                  : 'scale-110 group-hover:scale-115 group-hover:filter group-hover:brightness-110'
                                }`}
                                onError={() => handleImageError(category.id)}
                              />
                            ) : (
                              // Fallback לאייקון המקורי
                              <div className="flex flex-col items-center justify-center w-full h-full p-2">
                                <div className={`p-2 rounded-lg transition-all duration-500 ${
                                  isActive ? 'bg-white/20 shadow-lg' : 'bg-blue-500/20 group-hover:bg-blue-500/30'
                                }`}>
                                  <IconComponent className={`transition-all duration-500 ${
                                    isActive 
                                      ? 'h-11 w-11 md:h-14 md:w-14 text-cyan-300 drop-shadow-lg'
                                      : 'h-6 w-6 md:h-8 md:w-8 text-blue-200 group-hover:text-white'
                                  }`} />
                                </div>
                                {!isActive && (
                                  <div className="text-xs font-bold mt-1 text-center text-blue-100 group-hover:text-white leading-tight">
                                    {category.title}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>
                );
              })}
              
              {/* ברכה במרכז הכפתורים - רק במובייל כאשר אין קטגוריה פעילה */}
              {!hasActiveCategory && (
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10 pointer-events-none">
                  <span className="text-white text-base font-bold flex items-center justify-center gap-1 drop-shadow-lg">
                    {greeting.icon ? <greeting.icon className="w-5 h-5" /> : greeting.emoji} {greeting.text}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* בדסקטופ: העיצוב המקורי נשאר ללא שינוי! */}
          <div className="hidden md:block">
            {/* ברכה בדסקטופ - מתחת למלל ומעל הכפתורים */}
            <div className="text-center mb-4">
              <span className="text-white text-lg font-bold flex items-center justify-center gap-2">
                {greeting.icon ? <greeting.icon className="w-6 h-6" /> : greeting.emoji} {greeting.text} 
              </span>
            </div>
            
            <div className="flex gap-4 justify-center items-center max-w-5xl mx-auto">
              {savingsCategories.map((category, index) => {
                const IconComponent = category.icon;
                const isActive = selectedCategory === category.id;

                return (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryChange(category.id)}
                    className={`relative group transition-all duration-500 transform hover:scale-105 flex-shrink-0 ${isActive ? 'scale-105 z-20' : ''}`}
                  >
                    {/* אפקט זוהר חיצוני */}
                    <div className={`absolute -inset-1 rounded-xl blur-lg transition-all duration-500 ${isActive ? `bg-gradient-to-r ${category.gradient} opacity-50` : 'bg-white/10 opacity-0 group-hover:opacity-30'}`}></div>

                    <div className={`relative overflow-hidden rounded-xl transition-all duration-500 ${isActive ? `bg-gradient-to-br ${category.gradient} shadow-md` : 'bg-white/10 hover:bg-white/20 backdrop-blur-xl border-2 border-white/40 hover:border-white/60 shadow-md'} shadow-sky-500 p-3 w-36 h-32`}>
                      
                      {/* אפקט זוהר פנימי */}
                      {isActive && (
                        <div className="absolute inset-0 bg-gradient-to-r from-white/10 via-transparent to-white/10 animate-pulse"></div>
                      )}
                      
                      <div className="relative text-center">
                        <div className="flex justify-center mb-1">
                          <div className={`p-1.5 rounded-lg transition-all duration-500 ${isActive ? 'bg-white/20 shadow-lg' : 'bg-blue-500/20 group-hover:bg-blue-500/30'}`}>
                            <IconComponent className={`h-4 w-4 transition-all duration-500 ${isActive ? 'text-white' : 'text-blue-200 group-hover:text-white'}`} />
                          </div>
                        </div>
                        <div className={`text-xs font-bold mb-1 transition-colors duration-300 ${isActive ? 'text-white/90' : 'text-white/70'}`}>
                          השוואת
                        </div>
                        <div className={`text-m font-black mb-1 transition-all duration-300 ${isActive ? 'text-white' : 'text-blue-100 group-hover:text-white'}`}>
                          {category.title}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* שורת חיפוש משופרת - מותאמת למובייל */}
        <div className="w-full mx-auto mb-3 md:mb-4 px-3 md:px-0 md:max-w-4xl relative" style={{zIndex: 99999, overflow: 'visible'}}>
          <div style={{overflow: 'visible'}}>
            <OptimizedSearchAutocomplete
              selectedCategory={selectedCategory}
              onSearchSubmit={handleSearchResults}
              placeholder={currentCategory?.searchPlaceholder || (
                window.innerWidth < 768 ? '🔍 חפש מוצר...' : '🔍 חפש בכל מוצרי החיסכון...'
              )}
              loading={loading}
            />
          </div>
        </div>

        {/* אינדיקטור מקור הנתונים - מותאם למובייל */}
        {!currentSearchTerm && (
          <div className="text-center mb-2 md:mb-3 px-2 md:px-0">
            <div className="inline-flex flex-col md:flex-row items-center px-2 md:px-4 py-2 md:py-1 bg-green-500/20 backdrop-blur-xl rounded-2xl md:rounded-full border border-green-400/30 shadow-lg max-w-full">
              <CheckCircle className="h-3 w-3 md:h-4 md:w-4 ml-1 md:ml-0 md:mr-2 text-green-400 flex-shrink-0" />
              
              {currentCategory ? (
                <span className="font-bold text-green-200 text-xs md:text-sm text-center">
                  🎯 חיפוש ב{currentCategory.dataSource} - {currentCategory.description}
                </span>
              ) : (
                // במובייל: פריסה רטיקלית, בדסקטופ: שורה אחת
                <div className="text-center md:text-right">
                  {/* מובייל: כל שורה בנפרד */}
                  <div className="md:hidden space-y-1">
                    <div className="font-bold text-green-200 text-xs">
                      🌐 חיפוש בכל מקורות הנתונים הממשלתיים
                    </div>
                    <div className="text-green-200 text-xs">
                      גמל-נט / פנסיה-נט / ביטוח-נט
                    </div>
                    {formattedDate && (
                      <div className="text-green-300 text-xs font-medium">
                        📅 מעודכן ל{formattedDate}
                      </div>
                    )}
                  </div>
                  
                  {/* דסקטופ: שורה אחת */}
                  <div className="hidden md:block">
                    <span className="font-bold text-green-200 text-sm">
                      🌐 חיפוש בכל מקורות הנתונים הממשלתיים גמל-נט / פנסיה-נט / ביטוח-נט בשילוב פילוח נתונים והצגתם בבינה מלאכותית
                      {formattedDate && (
                        <span className="text-green-300 font-medium mr-2">
                          • מעודכן ל{formattedDate}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

      </div>
      
      {/* אלמנטים דקורטיביים תחתונים */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg className="w-full h-8 md:h-12 text-white/10" fill="currentColor" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"></path>
        </svg>
      </div>
    </section>
  );
});

export default Header;